import { useState } from 'react';

import { Folder, Search } from 'iconoir-react';
import { LibraryNavIcon } from 'components/blocks/layout/icons/library-nav-icon';
import { ListFilter, X } from 'lucide-react';
import { FilterType, HeaderToolbarConfig } from '../../types/header-toolbar.type';
import { useIsMobile } from 'hooks/use-mobile';
import { useTranslation } from 'react-i18next';
import { countActiveFilters } from '../common-filters';
import { HeaderActions } from './header-actions';
import AddDropdownMenu from '../file-manager-add-new-dropdown';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from 'components/ui/sheet';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { FilterControls } from './filter-controls';
import { ActiveFilters } from './active-filters';
import { ViewToggle } from './view-toggle';
import { useIsMediaLibrary } from 'hooks/use-is-media-library';
import { cn } from 'lib/utils';

const searchBarClassName =
  'flex h-12 w-full items-center gap-3 rounded-[8px] border border-solid border-[#e7e7e7] bg-white px-4 py-3 shadow-[0px_10px_10px_rgba(173,174,163,0.11)] dark:border-grey-400 dark:bg-card sm:gap-4 sm:px-5 sm:py-4 md:h-[54px]';

const librarySearchBarClassName =
  'flex h-12 w-full items-center gap-3 rounded-[8px] border border-solid border-[#e7e7e7] bg-white p-3 dark:border-grey-400 dark:bg-card';

export const BaseHeaderToolbar = <T extends FilterType>({
  title,
  subtitle,
  viewMode,
  searchQuery,
  filters,
  selectedItems = [],
  showFileUpload = false,
  showFolderCreate = false,
  filterConfigs,
  actions = [],
  onViewModeChange,
  onSearchChange,
  onFiltersChange,
  onFileUpload,
  onFolderCreate,
  showTitle = true,
}: HeaderToolbarConfig<T>) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const [openSheet, setOpenSheet] = useState(false);
  const isMediaLibrary = useIsMediaLibrary();

  const handleSearchChange = (query: string) => {
    onSearchChange(query);
    onFiltersChange({
      ...filters,
      name: query,
    } as T);
  };

  const handleResetFilters = () => {
    const resetFilters = Object.keys(filters).reduce((acc, key) => {
      acc[key] = key === 'name' ? '' : undefined;
      return acc;
    }, {} as Record<string, unknown>);

    onSearchChange('');
    onFiltersChange(resetFilters as T);
  };

  const activeFiltersCount = countActiveFilters(filters);
  const hasActiveFilters = activeFiltersCount > 0;

  const TitleIcon = isMediaLibrary ? LibraryNavIcon : Folder;

  const headerActions = (
    <div className="flex shrink-0 items-center justify-end gap-2 sm:gap-4">
      {!isMediaLibrary && (
        <ViewToggle viewMode={viewMode} onViewModeChange={onViewModeChange} className="h-8" />
      )}
      <HeaderActions actions={actions} selectedItems={selectedItems} isMobile={isMobile} />
      {(showFileUpload || showFolderCreate) && (
        <AddDropdownMenu
          isMobile={isMobile}
          onFileUpload={onFileUpload}
          onFolderCreate={onFolderCreate}
        />
      )}
    </div>
  );

  const searchField = (
    <div className={isMediaLibrary ? librarySearchBarClassName : searchBarClassName}>
      <div className="flex size-6 shrink-0 items-center justify-center overflow-hidden p-[3px] sm:size-8 sm:p-1">
        <Search
          width={isMediaLibrary ? 15 : 20}
          height={isMediaLibrary ? 15 : 20}
          strokeWidth={1.75}
          className="text-[#888888] dark:text-grey-400"
        />
      </div>
      <Input
        value={searchQuery}
        onChange={(event) => handleSearchChange(event.target.value)}
        placeholder={t('SEARCH_BY_FILE_NAME')}
        className="h-auto min-w-0 flex-1 border-0 bg-transparent p-0 font-sans text-sm font-normal not-italic leading-[1.2] tracking-normal text-grey-900 shadow-none placeholder:text-[#b0b0b0] focus-visible:ring-0 dark:text-foreground"
      />
      {isMobile ? (
        <Sheet open={openSheet} onOpenChange={setOpenSheet}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" className="relative size-8 shrink-0 rounded-full border-[#e7e7e7] dark:border-grey-400">
              <ListFilter className="size-4 dark:text-foreground" />
              {activeFiltersCount > 0 ? (
                <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
                  {activeFiltersCount}
                </span>
              ) : null}
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="w-full">
            <SheetHeader>
              <SheetTitle>{t('FILTERS')}</SheetTitle>
            </SheetHeader>
            <div className="py-4">
              <FilterControls
                filters={filters}
                filterConfigs={filterConfigs}
                onFiltersChange={onFiltersChange}
                isMobile={true}
              />
            </div>
            {hasActiveFilters ? (
              <Button variant="ghost" onClick={handleResetFilters} className="h-8 w-full px-2">
                {t('RESET')}
                <X className="ml-2 size-4" />
              </Button>
            ) : null}
          </SheetContent>
        </Sheet>
      ) : null}
    </div>
  );

  return (
    <div className="flex w-full shrink-0 flex-col items-center px-4 pb-4 pt-4 sm:px-6">
      <div
        className={cn(
          'flex w-full flex-col',
          isMediaLibrary ? 'max-w-[1060px] gap-6' : 'max-w-[829px] gap-6 md:gap-8'
        )}
      >
        {showTitle ? (
          <header
            className={cn(
              'flex w-full self-stretch',
              isMediaLibrary ? 'flex-col gap-2' : 'items-center justify-between gap-3'
            )}
          >
            <div
              className={cn(
                'flex min-w-0',
                isMediaLibrary ? 'flex-col gap-2' : 'h-8 flex-1 items-center gap-2 sm:w-[287px] sm:flex-none'
              )}
            >
              <div className="flex h-8 min-w-0 items-center gap-2">
                <div className="flex size-8 shrink-0 items-center justify-center">
                  <TitleIcon
                    width={isMediaLibrary ? 20 : 25}
                    height={isMediaLibrary ? 20 : 25}
                    strokeWidth={isMediaLibrary ? 1.5 : 1.75}
                    className="text-[#3d3d3d] dark:text-true-white"
                  />
                </div>
                <h1 className="min-w-0 max-w-full truncate whitespace-nowrap font-sans text-[24px] font-medium not-italic leading-[1.2] tracking-normal text-grey-900 dark:text-true-white">
                  {t(title)}
                </h1>
              </div>
              {isMediaLibrary && subtitle ? (
                <p className="min-w-0 text-sm font-normal leading-[1.6] text-[#3d3d3d] dark:text-muted-foreground sm:text-base">
                  {t(subtitle, {
                    defaultValue:
                      'Collections of media files and attachments that you have used in previous conversations',
                  })}
                </p>
              ) : null}
            </div>
            {!isMediaLibrary && headerActions}
          </header>
        ) : (
          <div className="flex w-full items-center justify-end">{headerActions}</div>
        )}

        <div className={cn('flex w-full flex-col', isMediaLibrary ? 'gap-6' : 'gap-4')}>
          {searchField}

          {!isMobile ? (
            <div
              className={cn(
                'flex items-center',
                isMediaLibrary ? 'h-[38px] justify-between gap-4' : ''
              )}
            >
              <FilterControls
                filters={filters}
                filterConfigs={filterConfigs}
                onFiltersChange={onFiltersChange}
                isMobile={false}
              />
              {isMediaLibrary ? (
                <ViewToggle viewMode={viewMode} onViewModeChange={onViewModeChange} />
              ) : null}
            </div>
          ) : null}

          <ActiveFilters
            filters={filters}
            filterConfigs={filterConfigs}
            onFiltersChange={onFiltersChange}
            onResetAll={handleResetFilters}
          />
        </div>
      </div>
    </div>
  );
};
